import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";
import { formatCurrency } from "../../types/booking";
import {
  calculateFinancialSummary,
  calculateBookingSummary,
  aggregateRevenueByMonth,
  aggregateBookingsByMonth,
  getPaymentMethodDistribution,
  getEventTypeDistribution,
  getStatusDistribution,
  filterBookingsByDateRange,
  calculateGrowthRate,
} from "../../utils/analyticsHelper";

const AnalyticsDashboard = ({ bookings }) => {
  const [dateRange, setDateRange] = useState("6months");

  // Filter bookings by selected date range
  const filteredBookings = useMemo(() => {
    return filterBookingsByDateRange(bookings, dateRange);
  }, [bookings, dateRange]);

  // Calculate summaries
  const financialSummary = useMemo(
    () => calculateFinancialSummary(filteredBookings),
    [filteredBookings]
  );

  const bookingSummary = useMemo(
    () => calculateBookingSummary(filteredBookings),
    [filteredBookings]
  );

  // Aggregate data for charts
  const revenueByMonth = useMemo(
    () => aggregateRevenueByMonth(filteredBookings, new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), new Date()),
    [filteredBookings]
  );

  const bookingsByMonth = useMemo(
    () => aggregateBookingsByMonth(filteredBookings, new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), new Date()),
    [filteredBookings]
  );

  const paymentMethods = useMemo(
    () => getPaymentMethodDistribution(filteredBookings),
    [filteredBookings]
  );

  const eventTypes = useMemo(
    () => getEventTypeDistribution(filteredBookings),
    [filteredBookings]
  );

  const statusDistribution = useMemo(
    () => getStatusDistribution(filteredBookings),
    [filteredBookings]
  );

  // Chart colors
  const COLORS = {
    primary: "#D4AF7A",
    secondary: "#C9A882",
    success: "#10B981",
    warning: "#F59E0B",
    danger: "#EF4444",
    info: "#3B82F6",
    purple: "#8B5CF6",
    pink: "#EC4899",
    cyan: "#06B6D4",
  };

  const CHART_COLORS = [COLORS.primary, COLORS.info, COLORS.success, COLORS.warning, COLORS.purple];

  return (
    <div className="space-y-8">
      {/* Header with Date Range Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-[#F7F5F2]">Analytics Dashboard</h2>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
        >
          <option value="6months">Last 6 Months</option>
          <option value="1year">Last Year</option>
          <option value="all">All Time</option>
        </select>
      </div>

      {/* Financial Analytics Section */}
      <section>
        <h3 className="text-lg font-semibold text-[#D4AF7A] mb-4">Financial Overview</h3>

        {/* Financial Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Revenue */}
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-[#D4AF7A]" />
              <span className="text-xs text-[#F7F5F2]/60">Total</span>
            </div>
            <p className="text-2xl font-bold text-[#F7F5F2]">
              {formatCurrency(financialSummary.totalRevenue)}
            </p>
            <p className="text-xs text-[#F7F5F2]/60 mt-1">All Time Revenue</p>
          </div>

          {/* Current Month Revenue */}
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-green-400" />
              <span className="text-xs text-[#F7F5F2]/60">This Month</span>
            </div>
            <p className="text-2xl font-bold text-[#F7F5F2]">
              {formatCurrency(financialSummary.currentMonthRevenue)}
            </p>
            <p className="text-xs text-[#F7F5F2]/60 mt-1">Current Month</p>
          </div>

          {/* Average Revenue */}
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 text-blue-400" />
              <span className="text-xs text-[#F7F5F2]/60">Average</span>
            </div>
            <p className="text-2xl font-bold text-[#F7F5F2]">
              {formatCurrency(financialSummary.averageRevenue)}
            </p>
            <p className="text-xs text-[#F7F5F2]/60 mt-1">Per Booking</p>
          </div>

          {/* Collection Rate */}
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <span className="text-xs text-[#F7F5F2]/60">Collected</span>
            </div>
            <p className="text-2xl font-bold text-[#F7F5F2]">
              {financialSummary.depositCollectionRate.toFixed(1)}%
            </p>
            <p className="text-xs text-[#F7F5F2]/60 mt-1">Collection Rate</p>
          </div>
        </div>

        {/* Revenue Trend Chart */}
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-[#F7F5F2] mb-4">Revenue Trend</h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueByMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(212, 175, 122, 0.2)" />
              <XAxis dataKey="month" stroke="#F7F5F2" />
              <YAxis stroke="#F7F5F2" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#0A0A0A",
                  border: "1px solid rgba(212, 175, 122, 0.3)",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#F7F5F2" }}
                formatter={(value) => formatCurrency(value)}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke={COLORS.primary}
                strokeWidth={2}
                dot={{ fill: COLORS.primary }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Payment Methods & Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Payment Methods Distribution */}
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-[#F7F5F2] mb-4">Payment Methods</h4>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={paymentMethods}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ method, percentage }) => `${method}: ${percentage.toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="amount"
                >
                  {paymentMethods.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0A0A0A",
                    border: "1px solid rgba(212, 175, 122, 0.3)",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#F7F5F2" }}
                  formatter={(value) => formatCurrency(value)}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Payment Status */}
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-[#F7F5F2] mb-4">Payment Status</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[#F7F5F2]/80">Total Collected</span>
                <span className="text-lg font-bold text-green-400">
                  {formatCurrency(financialSummary.totalPaid)}
                </span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2">
                <div
                  className="bg-green-400 h-2 rounded-full transition-all"
                  style={{
                    width: `${financialSummary.totalRevenue > 0 ? (financialSummary.totalPaid / financialSummary.totalRevenue) * 100 : 0}%`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#F7F5F2]/80">Pending Payments</span>
                <span className="text-lg font-bold text-yellow-400">
                  {formatCurrency(financialSummary.totalPendingPayments)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#F7F5F2]/80">Remaining Balance</span>
                <span className="text-lg font-bold text-blue-400">
                  {formatCurrency(financialSummary.totalRemaining)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Analytics Section */}
      <section>
        <h3 className="text-lg font-semibold text-[#D4AF7A] mb-4">Booking Analytics</h3>

        {/* Booking Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 text-[#D4AF7A]" />
              <span className="text-xs text-[#F7F5F2]/60">Total</span>
            </div>
            <p className="text-2xl font-bold text-[#F7F5F2]">{bookingSummary.totalBookings}</p>
            <p className="text-xs text-[#F7F5F2]/60 mt-1">All Bookings</p>
          </div>

          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-green-400" />
              <span className="text-xs text-[#F7F5F2]/60">This Month</span>
            </div>
            <p className="text-2xl font-bold text-[#F7F5F2]">{bookingSummary.currentMonthBookings}</p>
            <p className="text-xs text-[#F7F5F2]/60 mt-1">Current Month</p>
          </div>

          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <span className="text-xs text-[#F7F5F2]/60">Completed</span>
            </div>
            <p className="text-2xl font-bold text-[#F7F5F2]">{bookingSummary.completed}</p>
            <p className="text-xs text-[#F7F5F2]/60 mt-1">Successful</p>
          </div>

          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <XCircle className="w-8 h-8 text-red-400" />
              <span className="text-xs text-[#F7F5F2]/60">Cancelled</span>
            </div>
            <p className="text-2xl font-bold text-[#F7F5F2]">{bookingSummary.cancelled}</p>
            <p className="text-xs text-[#F7F5F2]/60 mt-1">Cancellations</p>
          </div>
        </div>

        {/* Booking Volume Chart */}
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6 mb-6">
          <h4 className="text-lg font-semibold text-[#F7F5F2] mb-4">Booking Volume</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={bookingsByMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(212, 175, 122, 0.2)" />
              <XAxis dataKey="month" stroke="#F7F5F2" />
              <YAxis stroke="#F7F5F2" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#0A0A0A",
                  border: "1px solid rgba(212, 175, 122, 0.3)",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#F7F5F2" }}
              />
              <Legend />
              <Bar dataKey="total" fill={COLORS.primary} name="Total Bookings" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Event Types & Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Event Type Distribution */}
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-[#F7F5F2] mb-4">Event Types</h4>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={eventTypes}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ type, percentage }) => `${type}: ${percentage.toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {eventTypes.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0A0A0A",
                    border: "1px solid rgba(212, 175, 122, 0.3)",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#F7F5F2" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Status Pipeline */}
          <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-[#F7F5F2] mb-4">Booking Status Pipeline</h4>
            <div className="space-y-3">
              {statusDistribution.distribution.map((status) => (
                <div key={status.status}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-[#F7F5F2]/80">{status.label}</span>
                    <span className="text-sm font-medium text-[#F7F5F2]">
                      {status.count} ({status.percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${status.percentage}%`,
                        backgroundColor:
                          status.status === "COMPLETED"
                            ? COLORS.success
                            : status.status === "CANCELLED"
                            ? COLORS.danger
                            : status.status === "CONFIRMED"
                            ? COLORS.info
                            : COLORS.warning,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Metrics */}
            <div className="mt-6 pt-4 border-t border-[#D4AF7A]/20">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#F7F5F2]/70">Completion Rate</span>
                <span className="text-lg font-bold text-green-400">
                  {statusDistribution.completedRate.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-[#F7F5F2]/70">Cancellation Rate</span>
                <span className="text-lg font-bold text-red-400">
                  {statusDistribution.cancellationRate.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AnalyticsDashboard;
